import { RestError } from '@azure/core-http';
import { Paths, ProjectPathParams } from '../../../app/router/app.router';
import { PermissionBoundary } from '../../../core/permissions/permission-boundary.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { useDeliveriesRequiringAttentionList } from './list/hooks/use-deliveries-requiring-attention-list.hook';
import { useMemo } from 'react';
import { makeDeliveriesRequiringAttentionListPageHeaderProps } from './list/logic/deliveries-requiring-attention-list-props.map';
import { DeliveriesRequiringAttentionTable } from './list/components/deliveries-requiring-attention-table.component';
import { Container } from '@mui/material';

export function DeliveriesRequiringAttentionPage({
  params
}: {
  params: ProjectPathParams<Paths.deliveriesRequiringAttention>;
}) {
  const { project, deliveries, infiniteScroll, formData, setFormData } = useDeliveriesRequiringAttentionList(
    params.projectID
  );

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );

  const error = deliveries.error;
  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  const headerProps = makeDeliveriesRequiringAttentionListPageHeaderProps(
    deliveries.data?.pages[0]?.totalRecords || 0,
    formData,
    setFormData
  );

  return (
    <Container>
      <PermissionBoundary
        availableUiFeatures={['internal-groundfloor']}
        notify={{
          render() {
            return <ErrorFullPage preset="ACCESS_DENIED" />;
          }
        }}
      >
        {project.data ? (
          <WindowTitle pageTitle="Deliveries Requiring Attention" detail={project.data.name} />
        ) : undefined}
        <ListPageHeader {...headerProps} />
        <DeliveriesRequiringAttentionTable
          params={params}
          pages={deliveries.data?.pages || []}
          infiniteScroll={infiniteScroll}
          setFormData={setFormData}
          sortData={sortData}
          isLoading={deliveries.isLoading}
          isPreviousData={deliveries.isPreviousData}
          hasError={deliveries.isError}
        />
      </PermissionBoundary>
    </Container>
  );
}
